import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, COLUMN_TYPES, type Column, type ColumnType } from '../lib/api';
import { COLUMN_TYPE_LABELS } from '../lib/ui';
import { keys } from '../lib/queries';
import { useToast } from './Toast';
import { useDialog } from './DialogHost';

export function ColumnPanel({
  docId, tableId, column, onClose,
}: {
  docId: string;
  tableId: string;
  column: Column;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const dialog = useDialog();

  const [label, setLabel] = useState(column.fields.label);
  const [type, setType] = useState<ColumnType>(column.fields.type);
  const [choices, setChoices] = useState((column.fields.widgetOptions?.choices ?? []).join('\n'));
  const [width, setWidth] = useState(160);

  useEffect(() => {
    setLabel(column.fields.label);
    setType(column.fields.type);
    setChoices((column.fields.widgetOptions?.choices ?? []).join('\n'));
  }, [column]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: keys.columns(docId, tableId) });
    qc.invalidateQueries({ queryKey: ['sizes', docId, tableId] });
  };

  const save = useMutation({
    mutationFn: async () => {
      await api.updateColumn(docId, tableId, column.fields.colId, {
        label,
        type,
        widgetOptions:
          type === 'Choice'
            ? { choices: choices.split('\n').map((c) => c.trim()).filter(Boolean) }
            : null,
      });
      await api.patchTableSizes(docId, tableId, {
        colWidths: [{ colId: column.fields.colId, width }],
      });
    },
    onSuccess: () => {
      invalidate();
      toast('Колонка обновлена', 'ok');
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'err'),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteColumn(docId, tableId, column.fields.colId),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: keys.records(docId, tableId) });
      toast('Колонка удалена', 'ok');
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'err'),
  });

  return (
    <aside
      className="rt-scroll"
      style={{
        width: 300,
        flex: '0 0 300px',
        background: 'var(--rt-panel)',
        borderLeft: '2px solid var(--rt-line)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <header className="rt-panel-head">
        <strong>Настройка колонки</strong>
        <span className="rt-spacer" />
        <button className="rt-btn" onClick={onClose}>✕</button>
      </header>

      <div style={{ padding: 14, display: 'grid', gap: 12 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="rt-count">Название</span>
          <input className="rt-input" value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>

        <div className="rt-count">
          Идентификатор: <code>{column.fields.colId}</code> — на него ссылаются
          формулы, он не меняется вместе с названием.
        </div>

        <label style={{ display: 'grid', gap: 4 }}>
          <span className="rt-count">Тип</span>
          <select
            className="rt-input"
            value={type}
            onChange={(e) => setType(e.target.value as ColumnType)}
          >
            {COLUMN_TYPES.map((t) => (
              <option key={t} value={t}>{COLUMN_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </label>

        {type === 'Choice' && (
          <label style={{ display: 'grid', gap: 4 }}>
            <span className="rt-count">Варианты — по одному в строке</span>
            <textarea
              className="rt-input"
              rows={5}
              style={{ height: 'auto', padding: 8 }}
              value={choices}
              onChange={(e) => setChoices(e.target.value)}
            />
          </label>
        )}

        <label style={{ display: 'grid', gap: 4 }}>
          <span className="rt-count">Ширина, px</span>
          <input
            className="rt-input"
            type="number"
            min={60}
            value={width}
            onChange={(e) => setWidth(Number(e.target.value))}
          />
        </label>

        {column.fields.isFormula && (
          <div className="rt-count">
            Колонка вычисляемая: <code>{column.fields.formula}</code>.
            Формула правится в строке формул над таблицей.
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="rt-btn rt-btn--acc"
            disabled={save.isPending || !label.trim()}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </button>
          <span className="rt-spacer" />
          <button
            className="rt-btn rt-btn--danger"
            disabled={remove.isPending}
            onClick={async () => {
              const ok = await dialog.confirm({
                title: 'Удалить колонку?',
                body: `Удалить колонку «${column.fields.label}» вместе со всеми данными?`,
                danger: true,
                confirmLabel: 'Удалить',
                cancelLabel: 'Отмена',
              });
              if (ok) {
                remove.mutate();
              }
            }}
          >
            Удалить
          </button>
        </div>
      </div>
    </aside>
  );
}
